import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ExternalLink, Globe, Users, Link2, TrendingUp, GraduationCap } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

// Official PCMA Institute logo
const PCMA_LOGO_URL = "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/cmvnbplv_pcma%20logo.png";

const translations = {
  overline: { es: "Alianza educativa global", en: "Global education partnership" },
  title: { es: "En alineación y con aval de PCMA Institute", en: "Aligned with and endorsed by PCMA Institute" },
  body: {
    es: "PCMA Institute es la división de educación profesional de PCMA — la red más grande de profesionales en la industria de eventos de negocios del mundo, con más de 9,200 miembros en 72 países. Los cursos en español ofrecidos a través de esta plataforma, han sido desarrollados en alineación y con el aval del marco educativo global de PCMA Institute, proporcionando a los profesionales latinoamericanos de la industria de eventos y reuniones, los conocimientos y habilidades reconocidos internacionalmente.",
    en: "PCMA Institute is the professional education division of PCMA — the world's largest network of business events professionals, with more than 9,200 members across 72 countries. The Spanish-language courses offered through this platform have been developed in alignment with and endorsed by PCMA Institute's global education framework, providing Latin American professionals in the events and meetings industry with internationally recognized knowledge and skills.",
  },
  cta: { es: "Más información en pcmainstitute.org", en: "Learn more at pcmainstitute.org" },
  logoAlt: { es: "Logo PCMA Institute", en: "PCMA Institute logo" },
  stats: {
    members: { es: "Miembros globales", en: "Global members" },
    countries: { es: "Países", en: "Countries" },
    framework: { es: "Marco educativo global", en: "Global education framework" },
  },
  benefitsTitle: {
    es: "Beneficios del aval PCMA",
    en: "Benefits of the PCMA endorsement",
  },
  benefitsSubtitle: {
    es: "Formación con reconocimiento y estándares globales que fortalecen a tu equipo, tu destino y tu organización.",
    en: "Training with global recognition and standards that strengthen your team, destination, and organization.",
  },
  socialProofCaption: {
    es: "Nuestra participación activa en el PCMA Mexico Chapter — Enero 2026",
    en: "Our active participation in the PCMA Mexico Chapter — January 2026",
  },
  socialProofBadge: {
    es: "Prueba social",
    en: "Social proof",
  },
  benefits: {
    es: [
      { icon: Globe, title: "Reconocimiento global", description: "Respaldo de la asociación internacional más prestigiosa en la industria de reuniones y eventos." },
      { icon: Users, title: "Red global de profesionales", description: "Acceso a una comunidad internacional de líderes y expertos en eventos y turismo de negocios." },
      { icon: Link2, title: "Oportunidades de conexión", description: "Vínculo directo con una red global de contactos, recursos y oportunidades de colaboración." },
      { icon: TrendingUp, title: "Impacto y crecimiento", description: "Herramientas y conocimientos para generar resultados medibles y sostenibles en destinos y organizaciones." },
      { icon: GraduationCap, title: "Educación de excelencia", description: "Estándares académicos alineados con las mejores prácticas y tendencias globales de la industria." },
    ],
    en: [
      { icon: Globe, title: "Global recognition", description: "Backing of the most prestigious international association in the meetings and events industry." },
      { icon: Users, title: "Global network of professionals", description: "Access to an international community of leaders and experts in events and business tourism." },
      { icon: Link2, title: "Connection opportunities", description: "Direct link to a global network of contacts, resources, and collaboration opportunities." },
      { icon: TrendingUp, title: "Impact and growth", description: "Tools and knowledge to generate measurable and sustainable results in destinations and organizations." },
      { icon: GraduationCap, title: "Education of excellence", description: "Academic standards aligned with the best global industry practices and trends." },
    ],
  },
  pillarsTitle: { es: "Tres pilares", en: "Three pillars" },
  pillars: {
    es: [
      { title: "Liderazgo global", description: "Líderes en la industria de reuniones y eventos." },
      { title: "Red mundial", description: "Conecta con profesionales y expertos en +90 países." },
      { title: "Resultados de negocio", description: "Herramientas y conocimientos para generar impacto real." },
    ],
    en: [
      { title: "Global leadership", description: "Leaders in the meetings and events industry." },
      { title: "Worldwide network", description: "Connect with professionals and experts in +90 countries." },
      { title: "Business results", description: "Tools and knowledge to generate real impact." },
    ],
  },
};

const PCMASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  return (
    <section
      id="pcma"
      data-testid="pcma-section"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#0B1F3B] overflow-hidden"
    >
      {/* Decorative gradient blobs */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#2ED3C6]/10 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#F59E0B]/10 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 text-white/70 text-xs md:text-sm uppercase tracking-[0.25em] font-bold font-body mb-4">
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
            {t(translations.overline)}
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
          </span>
          <h2
            data-testid="pcma-title"
            className="font-display text-3xl md:text-4xl lg:text-5xl text-white font-medium tracking-tight"
          >
            {t(translations.title)}
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 lg:p-16"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Logo */}
            <div className="lg:col-span-4 flex flex-col items-center lg:items-start">
              <div className="bg-white rounded-2xl p-8 md:p-10 w-full flex items-center justify-center shadow-2xl">
                <img
                  src={PCMA_LOGO_URL}
                  alt={t(translations.logoAlt)}
                  data-testid="pcma-logo"
                  className="h-20 md:h-24 lg:h-28 w-auto object-contain"
                  onError={(e) => {
                    // Fallback if logo not yet uploaded
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement.innerHTML =
                      '<div class="font-display text-3xl md:text-4xl text-[#0B1F3B] font-semibold tracking-tight text-center">PCMA<br/><span class="text-xl md:text-2xl font-medium text-[#2ED3C6]">Institute</span></div>';
                  }}
                />
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-4 mt-6 w-full">
                <div className="text-center lg:text-left">
                  <div className="font-display text-2xl md:text-3xl text-[#F59E0B] font-semibold">9,200+</div>
                  <div className="font-body text-xs text-white/60 uppercase tracking-wider mt-1">
                    {t(translations.stats.members)}
                  </div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="font-display text-2xl md:text-3xl text-[#2ED3C6] font-semibold">72</div>
                  <div className="font-body text-xs text-white/60 uppercase tracking-wider mt-1">
                    {t(translations.stats.countries)}
                  </div>
                </div>
              </div>
            </div>

            {/* Boilerplate copy */}
            <div className="lg:col-span-8">
              <p
                data-testid="pcma-boilerplate"
                className="font-body text-white/85 text-base md:text-lg leading-relaxed mb-8"
              >
                {t(translations.body)}
              </p>

              <a
                href="https://pcmainstitute.org"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pcma-link"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#2ED3C6] hover:bg-[#26b8ad] text-[#0B1F3B] font-display font-semibold rounded-full transition-all duration-300 hover:scale-105 group"
              >
                {t(translations.cta)}
                <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Social proof: PCMA Mexico Chapter photo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-16 md:mt-20"
        >
          <div className="relative rounded-3xl overflow-hidden bg-black border border-white/10 shadow-2xl">
            <img
              src="https://customer-assets-lxgj4vgw.emergentagent.net/job_mice-latam-pro/artifacts/sf1juwst_motion_photo_7042068062707180599.jpg"
              alt="Industria de Reuniones en el PCMA Mexico Chapter — Enero 2026"
              loading="lazy"
              decoding="async"
              className="w-full h-auto max-h-[500px] object-cover object-center"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />

            {/* Badge top-left */}
            <div className="absolute top-4 left-4 md:top-6 md:left-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B] text-[#0B1F3B] rounded-full text-[10px] font-display font-bold uppercase tracking-wider shadow-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0B1F3B]" />
                {t(translations.socialProofBadge)}
              </span>
            </div>

            {/* Caption bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <p className="font-display text-white text-base md:text-lg font-medium leading-snug max-w-2xl">
                {t(translations.socialProofCaption)}
              </p>
            </div>
          </div>
        </motion.div>

        {/* 5 Beneficios del aval PCMA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-16 md:mt-20"
        >
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl text-white font-medium tracking-tight mb-3">
              {t(translations.benefitsTitle)}
            </h3>
            <p className="font-body text-white/65 text-base leading-relaxed">
              {t(translations.benefitsSubtitle)}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t(translations.benefits).map((benefit, i) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 + i * 0.08 }}
                  data-testid={`pcma-benefit-${i + 1}`}
                  className="p-6 bg-white/[0.04] border border-white/10 rounded-2xl hover:bg-white/[0.08] hover:border-[#2ED3C6]/30 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#2ED3C6]/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#2ED3C6]" />
                  </div>
                  <h4 className="font-display text-lg text-white font-semibold mb-2 leading-tight">
                    {benefit.title}
                  </h4>
                  <p className="font-body text-white/65 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* 3 Pilares */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-14 md:mt-16 pt-10 border-t border-white/10"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {t(translations.pillars).map((pillar, i) => (
              <div key={i} data-testid={`pcma-pillar-${i + 1}`}>
                <div className="font-display text-xl md:text-2xl text-[#F59E0B] font-bold tracking-wide uppercase mb-2">
                  {pillar.title}
                </div>
                <p className="font-body text-white/70 text-sm leading-relaxed">
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PCMASection;
